/**
 * AP-40 7.4 — **Dashboard der Zeiterfassung**, die Startseite des Hauptmenüpunkts
 * (`/time-tracking/dashboard`; `/time-tracking` bleibt „Buchen", MD-5).
 *
 * Kein Umschalter „alle / meine" — die Kacheln sind schon getrennt. Stunden im Format des
 * Mandanten (Dezimal oder HH:MM) über `formatHours`; der Server liefert Minuten.
 */
import { Link } from 'react-router-dom'
import { Clock } from 'lucide-react'
import { DashboardKopf, DashboardListe, Kachel, KachelRaster, useDashboard } from '@/components/dashboard'
import { fmtDate } from '@/lib/format-date'
import { pageWide } from '@/lib/form-styles'
import { formatHoursWithUnit, useHoursFormat } from '@/lib/hours-format'
import { useTerminology } from '@/lib/useTerminology'
import { fetchTimeDashboard } from '../api/time-tracking.api'

interface Laufend {
  id: string
  name: string
  since: string
  sinceDate: string
  what: string | null
}

interface Abwesend {
  id: string
  date: string
  name: string
  type: string
}

export default function TimeDashboardPage() {
  const format = useHoursFormat()
  const terms = useTerminology()
  const { daten, fehler, kachel } = useDashboard('time_tracking', fetchTimeDashboard)
  const h = (min: unknown) => formatHoursWithUnit(typeof min === 'number' ? min : 0, format)
  const meine = kachel('myHours')
  const fehlzeit = kachel('absentToday')
  const berichte = kachel('monthlyReports')
  const arten = fehlzeit?.byType as Record<string, number> | undefined

  return (
    <div className={pageWide}>
      <DashboardKopf
        symbol={Clock}
        titel="Zeiterfassung"
        untertitel="Wer da ist, wer fehlt und wie viel gebucht ist."
        bereich="all"
        onBereich={() => undefined}
        umschalter={false}
        stand={daten?.stand}
        hilfe="zeiterfassung"
        aktion={
          <Link to="/time-tracking" className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground hover:opacity-90">
            <Clock className="h-4 w-4" /> Jetzt buchen
          </Link>
        }
      />

      {fehler && <div className="text-sm text-destructive">{fehler}</div>}

      <KachelRaster>
        <Kachel titel="Jetzt eingestempelt" kachel={kachel('runningNow')} />
        <Kachel
          titel={fehlzeit?.ownOnly ? 'Heute abwesend (ich)' : 'Heute abwesend'}
          kachel={fehlzeit}
          hinweis={arten && Object.keys(arten).length ? Object.entries(arten).map(([a, n]) => `${n} ${a}`).join(' · ') : undefined}
        />
        <Kachel titel="Meine Stunden diese Woche" kachel={meine} wert={h(meine?.weekMinutes)} hinweis={meine ? `diesen Monat ${h(meine.monthMinutes)}` : undefined} />
        <Kachel titel="Stunden aller diese Woche" kachel={kachel('allHours')} wert={h(kachel('allHours')?.minutes)} />
        <Kachel
          titel="Monatsberichte Vormonat"
          kachel={berichte}
          wert={berichte ? `${berichte.count} von ${berichte.of as number}` : undefined}
          hinweis={berichte ? `abgegeben für ${String(berichte.month).padStart(2, '0')}/${berichte.year as number}` : undefined}
        />
        <Kachel titel={`Stunden auf ${terms.projectPlural} heute`} kachel={kachel('projectHoursToday')} wert={h(kachel('projectHoursToday')?.minutes)} />
      </KachelRaster>

      <div className="grid gap-3 lg:grid-cols-2">
        <DashboardListe<Laufend>
          titel="Gerade eingestempelt"
          zeilen={daten?.lists?.running as Laufend[] | undefined}
          schluessel={(z) => z.id}
          alle="/time-tracking/current"
          leer="Niemand ist eingestempelt."
          zeile={(z) => (
            <>
              <span className="min-w-0 flex-1 truncate">{z.name}</span>
              <span className="shrink-0 text-xs text-muted-foreground">{z.what ?? ''}</span>
              <span className="w-16 shrink-0 text-right text-xs tabular-nums">seit {z.since}</span>
            </>
          )}
        />
        <DashboardListe<Abwesend>
          titel="Abwesend diese Woche"
          zeilen={daten?.lists?.absentThisWeek as Abwesend[] | undefined}
          schluessel={(z) => z.id}
          alle="/time-tracking/absences"
          leer="Diese Woche fehlt niemand."
          zeile={(z) => (
            <>
              <span className="w-20 shrink-0 text-xs text-muted-foreground">{fmtDate(z.date)}</span>
              <span className="min-w-0 flex-1 truncate">{z.name}</span>
              <span className="shrink-0 text-xs text-muted-foreground">{z.type}</span>
            </>
          )}
        />
      </div>
    </div>
  )
}
